import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import type { GuideSectionSummary, GuideSummary } from '@/lib/guideTemplate';
import { useCamera } from '@/hooks/useCamera';
import {
  normalizeTranscript,
  speakerStyles,
  type NormalizedTranscriptEntry,
  type TranscriptInput,
} from '../../lib/transcript';
import { MeetingView } from './MeetingView';

export type InterviewViewState = 'preparing' | 'active' | 'complete';

type AssistantMode = 'dashboard' | 'custom';

interface Badge {
  label: string;
  className: string;
  help?: string;
}

interface InterviewViewProps {
  state: InterviewViewState;
  sessionName: string;
  sessionNumber?: string;
  sessionDate?: string;
  sessionTopic?: string;
  sessionDescription?: string | null;
  participantName?: string;
  participantRole?: string;
  interviewerName?: string;
  interviewerSource?: string;
  statusBadge?: Badge;
  microphoneBadge?: Badge;
  topics?: string[];
  transcript?: TranscriptInput;
  elapsedLabel?: string;
  progressLabel?: string;
  progressValue?: number;
  isPaused?: boolean;
  isStarting?: boolean;
  isEnding?: boolean;
  canStart?: boolean;
  canPause?: boolean;
  canEnd?: boolean;
  onStart?: () => void;
  onPauseToggle?: () => void;
  onEnd?: () => void;
  onTestMicrophone?: () => void;
  isCheckingMicrophone?: boolean;
  assistantMode?: AssistantMode;
  canChangeAssistantMode?: boolean;
  onChangeAssistantMode?: (mode: AssistantMode) => void;
  onOpenAgentInfo?: () => void;
  customConfig?: ReactNode;
  highlights?: string[];
  nextSteps?: string[];
  onDownload?: () => void;
  onRestart?: () => void;
  isRestarting?: boolean;
  onCopyHighlights?: () => void;
  templateMode?: boolean;
  templateNotice?: string;
  guideSummary?: GuideSummary;
  guideSections?: GuideSectionSummary[];
  guideContext?: string | null;
  error?: string | null;
}

const stateCopy: Record<InterviewViewState, { title: string; subtitle: string }> = {
  preparing: {
    title: 'Preparing the interview room',
    subtitle: 'Review the focus areas and start when everything feels ready.',
  },
  active: {
    title: 'Interview in progress',
    subtitle: 'Capture the handoff live while Kaiva guides the conversation.',
  },
  complete: {
    title: 'Interview wrapped',
    subtitle: 'Review highlights and export everything your team needs.',
  },
};

function formatTimestamp(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return new Date().toLocaleTimeString();
  }

  return parsed.toLocaleTimeString();
}

function formatGuideDate(value: string | null | undefined): string {
  if (!value) {
    return '---';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '---';
  }

  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) {
    return text;
  }

  return `${text.slice(0, Math.max(0, maxLength - 3)).trimEnd()}...`;
}

function ProgressBar({ value }: { value: number }) {
  const clamped = Math.max(0, Math.min(100, value));
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
      <div
        className="h-full rounded-full bg-kaiva-coral transition-all duration-300"
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}

function SectionCard({
  title,
  children,
  className,
}: {
  title: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn(
        'rounded-2xl border border-border bg-card/90 p-6 shadow-sm',
        className
      )}
    >
      <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
        {title}
      </h3>
      <div className="mt-4 space-y-3 text-sm text-foreground">{children}</div>
    </section>
  );
}

function TranscriptPanel({
  title,
  subtitle,
  entries,
  autoScroll = true,
  liveBadge,
  className,
  emptyMessage,
}: {
  title: string;
  subtitle?: string;
  entries: NormalizedTranscriptEntry[];
  autoScroll?: boolean;
  liveBadge?: string;
  className?: string;
  emptyMessage?: string;
}) {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const lastEntry = entries[entries.length - 1];

  useEffect(() => {
    if (!autoScroll || !containerRef.current || entries.length === 0) {
      return;
    }

    const container = containerRef.current;
    container.scrollTop = container.scrollHeight;
  }, [autoScroll, entries.length, lastEntry?.text, lastEntry?.timestamp]);

  const copyTranscript = async () => {
    const text = entries
      .map((entry) => {
        const speakerLabel =
          entry.speaker === 'assistant'
            ? 'Kaiva'
            : entry.speaker === 'user'
              ? 'You'
              : 'System';
        return `[${formatTimestamp(entry.timestamp)}] ${speakerLabel}: ${entry.text}`;
      })
      .join('\n');

    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error('Clipboard is not available');
      }
      await navigator.clipboard.writeText(text);
      setCopyState('copied');
      window.setTimeout(() => setCopyState('idle'), 1500);
    } catch {
      setCopyState('error');
      window.setTimeout(() => setCopyState('idle'), 1500);
    }
  };

  return (
    <section
      className={cn(
        'flex h-full min-h-[360px] flex-col rounded-2xl border border-border bg-card/95 p-6 shadow-sm',
        className
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="text-base font-semibold text-foreground">{title}</h3>
            {liveBadge && (
              <span className="rounded-full border border-kaiva-coral/40 bg-kaiva-coral/10 px-2.5 py-0.5 text-xs font-semibold text-kaiva-coral">
                {liveBadge}
              </span>
            )}
          </div>
          {subtitle && <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>}
        </div>
        <button
          type="button"
          onClick={copyTranscript}
          disabled={entries.length === 0}
          className="rounded-full border border-border bg-card px-3 py-1 text-xs font-semibold text-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
        >
          {copyState === 'copied'
            ? 'Copied'
            : copyState === 'error'
              ? 'Copy failed'
              : 'Copy'}
        </button>
      </div>

      <div
        ref={containerRef}
        className="mt-4 flex-1 space-y-3 overflow-y-auto rounded-xl border border-border bg-muted/50 p-4"
      >
        {entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {emptyMessage ?? 'No transcript yet.'}
          </p>
        ) : (
          entries.map((entry) => (
            <article
              key={entry.id}
              className={cn('rounded-xl border px-4 py-3', speakerStyles[entry.speaker])}
            >
              <div className="mb-2 flex items-center justify-between text-xs font-semibold">
                <span>
                  {entry.speaker === 'assistant'
                    ? 'Kaiva'
                    : entry.speaker === 'user'
                      ? 'You'
                      : 'System'}
                </span>
                <span>{formatTimestamp(entry.timestamp)}</span>
              </div>
              <p className="text-sm leading-relaxed">{entry.text}</p>
            </article>
          ))
        )}
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────
   CameraCheckStep — Step 2: Audio & Video Check
   ────────────────────────────────────────── */

interface CameraCheckStepProps {
  microphoneBadge?: { label: string; className: string; help?: string };
  onTestMicrophone?: () => void;
  isCheckingMicrophone?: boolean;
  assistantMode?: 'dashboard' | 'custom';
  canChangeAssistantMode?: boolean;
  onChangeAssistantMode?: (mode: 'dashboard' | 'custom') => void;
  interviewerSource?: string;
  onOpenAgentInfo?: () => void;
  customConfig?: ReactNode;
  onBack: () => void;
  onNext: () => void;
}

function CameraCheckStep({
  microphoneBadge,
  onTestMicrophone,
  isCheckingMicrophone,
  assistantMode,
  canChangeAssistantMode,
  onChangeAssistantMode,
  interviewerSource,
  onOpenAgentInfo,
  customConfig,
  onBack,
  onNext,
}: CameraCheckStepProps) {
  const camera = useCamera();
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Attach stream to video element
  useEffect(() => {
    if (videoRef.current && camera.stream) {
      videoRef.current.srcObject = camera.stream;
    }
  }, [camera.stream]);

  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="text-xl font-semibold text-foreground">Check your audio & video</p>
        <p className="mt-2 text-sm text-muted-foreground">
          Make sure your microphone and camera are working before you join.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Left: Mic + Camera check + advanced options */}
        <div className="space-y-4">
          {/* Mic check card */}
          <div className="overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-kaiva-teal/5 via-background to-kaiva-coral/5 p-6">
            <h4 className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              Microphone
            </h4>
            <div className="flex items-start gap-6">
              <div className="relative flex h-20 w-20 flex-shrink-0 items-center justify-center rounded-2xl border border-border bg-white/80 shadow-sm">
                <span className="absolute h-14 w-14 rounded-full bg-kaiva-coral/10" />
                <span className="absolute h-8 w-8 rounded-full bg-kaiva-coral/20" />
                <span className="relative h-4 w-4 rounded-full bg-kaiva-coral" />
              </div>
              <div className="flex-1 space-y-3">
                {microphoneBadge && (
                  <div>
                    <span
                      className={cn(
                        'inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold',
                        microphoneBadge.className
                      )}
                    >
                      {microphoneBadge.label}
                    </span>
                    {microphoneBadge.help && (
                      <p className="mt-2 text-sm text-muted-foreground">{microphoneBadge.help}</p>
                    )}
                  </div>
                )}
                {onTestMicrophone && (
                  <button
                    type="button"
                    onClick={onTestMicrophone}
                    disabled={isCheckingMicrophone}
                    className="rounded-full border border-border bg-card px-5 py-2 text-sm font-semibold text-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isCheckingMicrophone ? 'Checking…' : 'Test microphone'}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Camera check card */}
          <div className="overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-kaiva-teal/5 via-background to-kaiva-coral/5 p-6">
            <h4 className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              Camera
            </h4>
            <div className="flex items-start gap-6">
              {/* Camera preview or placeholder */}
              <div className="relative flex h-32 w-44 flex-shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-border bg-muted shadow-sm">
                {camera.isActive ? (
                  <video
                    ref={videoRef as React.LegacyRef<HTMLVideoElement>}
                    autoPlay
                    playsInline
                    muted
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <span className="text-3xl">📷</span>
                    <span className="text-xs">Camera off</span>
                  </div>
                )}
              </div>
              <div className="flex-1 space-y-3">
                <div>
                  <span
                    className={cn(
                      'inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold',
                      camera.isActive
                        ? 'border-kaiva-success/40 bg-kaiva-success/10 text-kaiva-success'
                        : camera.error
                          ? 'border-kaiva-coral/40 bg-kaiva-coral/10 text-kaiva-coral'
                          : 'border-border bg-muted text-foreground'
                    )}
                  >
                    {camera.isActive
                      ? 'Camera Ready'
                      : camera.error
                        ? 'Camera Blocked'
                        : 'Camera Off'}
                  </span>
                  {camera.error && (
                    <p className="mt-2 text-sm text-muted-foreground">
                      {camera.error}
                    </p>
                  )}
                  {!camera.isActive && !camera.error && (
                    <p className="mt-2 text-sm text-muted-foreground">
                      Turn on your camera to enable video during the interview. This is optional.
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => void camera.toggle()}
                  className={cn(
                    'rounded-full border px-5 py-2 text-sm font-semibold transition',
                    camera.isActive
                      ? 'border-kaiva-coral/40 bg-kaiva-coral/10 text-kaiva-coral hover:bg-kaiva-coral/20'
                      : 'border-border bg-card text-foreground hover:bg-muted'
                  )}
                >
                  {camera.isActive ? 'Turn off camera' : 'Test camera'}
                </button>
              </div>
            </div>
          </div>

          {/* Interviewer mode (dev/advanced) */}
          {assistantMode && onChangeAssistantMode && (
            <div className="rounded-2xl border border-border bg-card p-5">
              <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                Interviewer
              </h3>
              <div className="mt-4 space-y-3">
                {interviewerSource && (
                  <p className="text-xs text-muted-foreground">{interviewerSource}</p>
                )}
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => onChangeAssistantMode('dashboard')}
                    disabled={!canChangeAssistantMode}
                    className={cn(
                      'rounded-full border px-3 py-1 text-xs font-semibold transition',
                      assistantMode === 'dashboard'
                        ? 'border-kaiva-teal bg-kaiva-teal text-white'
                        : 'border-border bg-card text-foreground hover:bg-muted',
                      !canChangeAssistantMode && 'cursor-not-allowed opacity-60'
                    )}
                  >
                    Dashboard
                  </button>
                  <button
                    type="button"
                    onClick={() => onChangeAssistantMode('custom')}
                    disabled={!canChangeAssistantMode}
                    className={cn(
                      'rounded-full border px-3 py-1 text-xs font-semibold transition',
                      assistantMode === 'custom'
                        ? 'border-kaiva-teal bg-kaiva-teal text-white'
                        : 'border-border bg-card text-foreground hover:bg-muted',
                      !canChangeAssistantMode && 'cursor-not-allowed opacity-60'
                    )}
                  >
                    Custom
                  </button>
                </div>
                {onOpenAgentInfo && (
                  <button
                    type="button"
                    onClick={onOpenAgentInfo}
                    className="inline-flex items-center rounded-full border border-border bg-card px-3 py-1 text-xs font-semibold text-foreground transition hover:bg-muted"
                  >
                    View agent info
                  </button>
                )}
              </div>
            </div>
          )}

          {customConfig && (
            <details className="rounded-2xl border border-border bg-card/95 p-6 shadow-sm">
              <summary className="cursor-pointer text-sm font-semibold text-foreground">
                Advanced configuration
              </summary>
              <div className="mt-6">{customConfig}</div>
            </details>
          )}
        </div>

        {/* Right: Before you join checklist */}
        <div className="rounded-2xl border border-border bg-card p-6">
          <h3 className="text-sm font-semibold text-foreground">Before you join</h3>
          <ul className="mt-4 space-y-3">
            {[
              'Find a quiet place where you can speak freely without interruptions.',
              'Make sure your microphone is working — use the test button on the left.',
              'Optionally turn on your camera for a more personal interview experience.',
              'Kaiva will guide the conversation. Just speak naturally.',
              'Your responses are automatically recorded and transcribed.',
              'You can pause or end the interview at any time.',
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm text-muted-foreground">
                <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-kaiva-teal/10 text-[11px] font-bold text-kaiva-teal">
                  ✓
                </span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-border pt-4">
        <button
          type="button"
          onClick={onBack}
          className="rounded-full border border-border bg-card px-6 py-3 text-sm font-semibold text-foreground transition hover:bg-muted"
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={onNext}
          className="rounded-full bg-kaiva-teal px-8 py-3 text-sm font-semibold text-white transition hover:bg-kaiva-teal/90"
        >
          Next: Join →
        </button>
      </div>
    </div>
  );
}

export function InterviewView({
  state,
  sessionName,
  sessionNumber,
  sessionDate,
  sessionTopic,
  sessionDescription,
  participantName,
  participantRole,
  interviewerName,
  interviewerSource,
  statusBadge,
  microphoneBadge,
  topics,
  transcript,
  elapsedLabel,
  progressLabel: progressLabelProp,
  progressValue: progressValueProp,
  isPaused,
  isStarting,
  isEnding,
  canStart,
  canPause,
  canEnd,
  onStart,
  onPauseToggle,
  onEnd,
  onTestMicrophone,
  isCheckingMicrophone,
  assistantMode,
  canChangeAssistantMode,
  onChangeAssistantMode,
  onOpenAgentInfo,
  customConfig,
  highlights,
  nextSteps,
  onDownload,
  onRestart,
  isRestarting = false,
  onCopyHighlights,
  templateMode,
  templateNotice,
  guideSummary,
  guideSections,
  guideContext,
  error,
}: InterviewViewProps) {
  const transcriptEntries = useMemo(() => normalizeTranscript(transcript), [transcript]);
  const transcriptText = useMemo(
    () => transcriptEntries.map((entry) => entry.text.toLowerCase()).join(' '),
    [transcriptEntries]
  );

  const topicCoverage = useMemo(() => {
    if (!topics || topics.length === 0) {
      return { covered: 0, total: 0 };
    }

    const covered = topics.filter((topic) => {
      const normalized = topic.trim().toLowerCase();
      if (!normalized) {
        return false;
      }
      return transcriptText.includes(normalized);
    }).length;

    return { covered, total: topics.length };
  }, [topics, transcriptText]);

  const resolvedProgressValue =
    typeof progressValueProp === 'number'
      ? Math.max(0, Math.min(100, progressValueProp))
      : topicCoverage.total > 0
        ? Math.round((topicCoverage.covered / topicCoverage.total) * 100)
        : state === 'active'
          ? 18
          : 0;

  const resolvedProgressLabel =
    progressLabelProp ??
    (topicCoverage.total > 0
      ? `${topicCoverage.covered} of ${topicCoverage.total} topics covered`
      : state === 'active'
        ? 'Interview running'
        : state === 'complete'
          ? 'Interview complete'
          : 'Ready to begin');

  const resolvedHighlights = useMemo(() => {
    if (highlights && highlights.length > 0) {
      return highlights;
    }

    const recent = transcriptEntries
      .filter((entry) => entry.speaker !== 'system')
      .slice(-3)
      .map((entry) => truncateText(entry.text, 120));

    return recent;
  }, [highlights, transcriptEntries]);

  const resolvedNextSteps = useMemo(
    () =>
      nextSteps ?? [
        'Share the transcript with the project lead.',
        'Tag decisions that need confirmation.',
        'Schedule any follow-up handoff sessions.',
      ],
    [nextSteps]
  );

  const guideMetaItems = useMemo(() => {
    if (!guideSummary) {
      return [];
    }

    return [
      { label: 'Status', value: guideSummary.status },
      { label: 'Version', value: guideSummary.version },
      { label: 'Created', value: formatGuideDate(guideSummary.createdAt) },
      { label: 'Updated', value: formatGuideDate(guideSummary.updatedAt) },
      { label: 'Created by', value: guideSummary.createdBy },
    ];
  }, [guideSummary]);

  const structureSummaryItems = useMemo(() => {
    if (!guideSummary) {
      return [];
    }

    return [
      { label: 'Sections', value: `${guideSummary.sectionCount}` },
      { label: 'Blocks', value: `${guideSummary.blockCount}` },
      {
        label: 'Required / Optional',
        value: `${guideSummary.requiredBlockCount} / ${guideSummary.optionalBlockCount}`,
      },
      { label: 'Estimated Time', value: `${guideSummary.estimatedMinutes} min` },
      { label: 'Sessions', value: `${guideSummary.sessionCount}` },
    ];
  }, [guideSummary]);

  const guideContextValue = guideContext?.trim() ?? '';

  const [highlightCopyState, setHighlightCopyState] = useState<'idle' | 'copied' | 'error'>(
    'idle'
  );
  const [preparingStep, setPreparingStep] = useState<1 | 2 | 3>(1);

  const copyHighlights = async () => {
    if (onCopyHighlights) {
      onCopyHighlights();
      return;
    }

    if (!resolvedHighlights || resolvedHighlights.length === 0) {
      return;
    }

    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error('Clipboard is not available');
      }
      await navigator.clipboard.writeText(resolvedHighlights.join('\n'));
      setHighlightCopyState('copied');
      window.setTimeout(() => setHighlightCopyState('idle'), 1500);
    } catch {
      setHighlightCopyState('error');
      window.setTimeout(() => setHighlightCopyState('idle'), 1500);
    }
  };

  const stateLabel =
    state === 'active' ? 'Live' : state === 'complete' ? 'Complete' : 'Preparing';

  const isLive = state === 'active';
  const shouldAnimateVoice = isLive && !isPaused;
  const interviewTitle = isLive ? 'Interview in progress' : 'Session Readiness';
  const interviewSubtitle = isLive
    ? 'Kaiva is guiding the conversation live. Speak naturally to capture the handover.'
    : 'Check your audio setup and start when you are ready.';
  const transcriptSubtitle = isLive
    ? 'Kaiva writes the conversation in real time.'
    : 'Transcript appears as soon as the interview starts.';
  const transcriptEmptyMessage = isLive
    ? 'Listening for the first response...'
    : 'Start the interview to see the transcript here.';

  const guideSummaryCard = guideSummary ? (
    <SectionCard title="Guide Summary" className="p-8">
      <div className="grid gap-4 sm:grid-cols-2">
        {guideMetaItems.map((item) => (
          <div key={item.label}>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              {item.label}
            </p>
            <p className="mt-2 text-sm font-semibold text-foreground">{item.value}</p>
          </div>
        ))}
      </div>
    </SectionCard>
  ) : null;

  const guideStructureCard =
    guideSections && guideSections.length > 0 ? (
      <SectionCard title="Guide Structure" className="p-8">
        <div className="space-y-3">
          {guideSections.map((section) => (
            <div
              key={section.id}
              className="rounded-xl border border-border bg-muted/60 px-4 py-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-foreground">{section.title}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {section.blockCount} blocks
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs font-semibold text-foreground">
                    {section.estimatedMinutes} min
                  </p>
                  {section.sessionBreakpoint && (
                    <span className="mt-2 inline-flex rounded-full border border-kaiva-coral/40 bg-kaiva-coral/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-kaiva-coral">
                      Session break
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>
    ) : null;

  const structureSummaryCard =
    structureSummaryItems.length > 0 ? (
      <SectionCard title="Structure Summary" className="p-8">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {structureSummaryItems.map((item) => (
            <div key={item.label}>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                {item.label}
              </p>
              <p className="mt-2 text-sm font-semibold text-foreground">{item.value}</p>
            </div>
          ))}
        </div>
      </SectionCard>
    ) : null;

  const guideContextPanel = guideContextValue ? (
    <details className="rounded-2xl border border-border bg-card/95 p-6 shadow-sm">
      <summary className="cursor-pointer text-sm font-semibold text-foreground">
        Guide Context
      </summary>
      <p className="mt-4 whitespace-pre-line text-sm text-muted-foreground">
        {guideContextValue}
      </p>
    </details>
  ) : null;

  const developerDetailsPanel = guideSummary?.structureId ? (
    <details className="rounded-2xl border border-border bg-card/95 p-6 shadow-sm">
      <summary className="cursor-pointer text-sm font-semibold text-foreground">
        Developer details
      </summary>
      <div className="mt-4">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Structure ID</p>
        <p className="mt-2 break-all rounded-lg border border-border bg-muted/60 p-3 text-xs text-foreground">
          {guideSummary.structureId}
        </p>
      </div>
    </details>
  ) : null;

  const interviewFocusCard = (
    <section className="rounded-3xl border border-kaiva-coral/20 bg-gradient-to-br from-white via-white to-kaiva-coral/5 p-8 shadow-sm lg:p-10">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            {interviewTitle}
          </p>
          <h2 className="mt-2 text-2xl font-semibold text-foreground md:text-3xl">
            {isLive ? 'Kaiva is live with you' : 'Ready to begin'}
          </h2>
          <p className="mt-3 text-sm text-muted-foreground">{interviewSubtitle}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isLive && (
            <span className="inline-flex items-center gap-2 rounded-full border border-kaiva-coral/40 bg-kaiva-coral/15 px-3 py-1 text-xs font-semibold text-kaiva-coral">
              <span className="h-2 w-2 rounded-full bg-kaiva-coral animate-pulse" />
              Live
            </span>
          )}
          {statusBadge && (
            <span
              className={cn(
                'inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold',
                statusBadge.className
              )}
            >
              {statusBadge.label}
            </span>
          )}
          {microphoneBadge && (
            <span
              className={cn(
                'inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold',
                microphoneBadge.className
              )}
            >
              {microphoneBadge.label}
            </span>
          )}
        </div>
      </div>

      {microphoneBadge?.help && (
        <p className="mt-3 text-xs text-muted-foreground">{microphoneBadge.help}</p>
      )}

      <div className="mt-8 grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
        <div className="space-y-6">
          <div className="grid gap-6 sm:grid-cols-2">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                {isLive ? 'Elapsed' : 'Timer'}
              </p>
              <p className="mt-2 text-4xl font-semibold text-foreground md:text-5xl">
                {elapsedLabel ?? '00:00'}
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                {sessionTopic ?? 'Handover interview'}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Progress</p>
              <p className="mt-2 text-xs font-semibold text-muted-foreground">
                {resolvedProgressLabel}
              </p>
              <div className="mt-3">
                <ProgressBar value={resolvedProgressValue} />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4 rounded-2xl border border-border bg-white/70 p-4">
            <div className="relative flex h-14 w-14 items-center justify-center">
              <span
                className={cn(
                  'absolute h-14 w-14 rounded-full bg-kaiva-coral/15',
                  shouldAnimateVoice && 'kaiva-orb'
                )}
              />
              <span className="absolute h-8 w-8 rounded-full bg-kaiva-coral/20" />
              <span className="relative h-4 w-4 rounded-full bg-kaiva-coral" />
            </div>
            <div className="flex-1">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">AI Voice</p>
              <div className="mt-2 flex items-end gap-1">
                {Array.from({ length: 5 }).map((_, index) => (
                  <span
                    key={`wave-${index}`}
                    className={cn(
                      'h-5 w-1.5 rounded-full bg-kaiva-coral/70',
                      shouldAnimateVoice && 'kaiva-wave',
                      index === 1 && shouldAnimateVoice && 'kaiva-wave-delay-1',
                      index === 2 && shouldAnimateVoice && 'kaiva-wave-delay-2',
                      index === 3 && shouldAnimateVoice && 'kaiva-wave-delay-3'
                    )}
                  />
                ))}
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                {isLive ? (isPaused ? 'Paused' : 'Kaiva is speaking') : 'Ready to speak'}
              </p>
            </div>
          </div>

          {!isLive && (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={onStart}
                disabled={!canStart || !onStart}
                className="w-full rounded-full bg-kaiva-coral px-6 py-4 text-sm font-semibold text-white transition hover:bg-kaiva-coral/90 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {isStarting ? 'Starting...' : 'Start Interview'}
              </button>
              {onTestMicrophone && (
                <button
                  type="button"
                  onClick={onTestMicrophone}
                  disabled={isCheckingMicrophone}
                  className="w-full rounded-full border border-border bg-card px-5 py-4 text-sm font-semibold text-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  {isCheckingMicrophone ? 'Checking microphone...' : 'Test Microphone'}
                </button>
              )}
            </div>
          )}

          {isLive && (
            <div className="flex flex-col gap-3 sm:flex-row">
              {onPauseToggle && (
                <button
                  type="button"
                  onClick={onPauseToggle}
                  disabled={!canPause}
                  className="w-full rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold text-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  {isPaused ? 'Resume' : 'Pause'}
                </button>
              )}
              {onEnd && (
                <button
                  type="button"
                  onClick={onEnd}
                  disabled={!canEnd || isEnding}
                  className="w-full rounded-full bg-kaiva-coral px-5 py-3 text-sm font-semibold text-white transition hover:bg-kaiva-coral/90 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  {isEnding ? 'Ending...' : 'End Interview'}
                </button>
              )}
            </div>
          )}
        </div>

        <TranscriptPanel
          title={isLive ? 'Live Transcript' : 'Transcript'}
          subtitle={transcriptSubtitle}
          entries={transcriptEntries}
          autoScroll={isLive}
          liveBadge={isLive ? 'Live' : undefined}
          emptyMessage={transcriptEmptyMessage}
          className="min-h-[320px] max-h-[520px]"
        />
      </div>
    </section>
  );

  const preparingStepIndicator = (
    <div className="flex items-center">
      {/* Step 1 */}
      <button
        type="button"
        onClick={() => setPreparingStep(1)}
        className="flex flex-shrink-0 items-center gap-2"
      >
        <span
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition',
            preparingStep === 1
              ? 'bg-kaiva-coral text-white'
              : 'bg-kaiva-teal text-white'
          )}
        >
          {preparingStep > 1 ? '✓' : '1'}
        </span>
        <span
          className={cn(
            'hidden text-sm font-medium sm:block',
            preparingStep === 1 ? 'text-foreground' : 'text-muted-foreground'
          )}
        >
          Instructions
        </span>
      </button>

      <div className={cn('mx-3 h-px flex-1', preparingStep > 1 ? 'bg-kaiva-teal' : 'bg-border')} />

      {/* Step 2 */}
      <button
        type="button"
        onClick={() => preparingStep > 1 && setPreparingStep(2)}
        className={cn('flex flex-shrink-0 items-center gap-2', preparingStep < 2 && 'cursor-default')}
      >
        <span
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition',
            preparingStep === 2
              ? 'bg-kaiva-coral text-white'
              : preparingStep > 2
                ? 'bg-kaiva-teal text-white'
                : 'border border-border bg-muted text-muted-foreground'
          )}
        >
          {preparingStep > 2 ? '✓' : '2'}
        </span>
        <span
          className={cn(
            'hidden text-sm font-medium sm:block',
            preparingStep === 2 ? 'text-foreground' : 'text-muted-foreground'
          )}
        >
          Audio & Video
        </span>
      </button>

      <div className={cn('mx-3 h-px flex-1', preparingStep > 2 ? 'bg-kaiva-teal' : 'bg-border')} />

      {/* Step 3 */}
      <button
        type="button"
        onClick={() => preparingStep > 2 && setPreparingStep(3)}
        className={cn('flex flex-shrink-0 items-center gap-2', preparingStep < 3 && 'cursor-default')}
      >
        <span
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition',
            preparingStep === 3
              ? 'bg-kaiva-coral text-white'
              : 'border border-border bg-muted text-muted-foreground'
          )}
        >
          3
        </span>
        <span
          className={cn(
            'hidden text-sm font-medium sm:block',
            preparingStep === 3 ? 'text-foreground' : 'text-muted-foreground'
          )}
        >
          Join
        </span>
      </button>
    </div>
  );

  const preparingContent = (
    <div className="flex flex-col gap-5">
      {preparingStepIndicator}

      {/* ── Step 1: Instructions & Agenda ── */}
      {preparingStep === 1 && (
        <div className="flex flex-col gap-4">
          <div>
            <p className="text-xl font-semibold text-foreground">
              Hi{participantName ? `, ${participantName.split(' ')[0]}` : ''}!
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              {sessionDescription
                ? sessionDescription
                : "You've been invited to a knowledge-capture voice interview. Kaiva will guide the conversation — just speak naturally."}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Left: Interview Details + Participants */}
            <div className="space-y-4">
              <div className="rounded-2xl border border-border bg-card p-5">
                <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                  Interview Details
                </h3>
                <div className="mt-4 space-y-3">
                  {sessionTopic && (
                    <div>
                      <p className="text-xs text-muted-foreground">Topic</p>
                      <p className="mt-0.5 text-sm font-semibold text-foreground">{sessionTopic}</p>
                    </div>
                  )}
                  {sessionDate && sessionDate !== '---' && (
                    <div>
                      <p className="text-xs text-muted-foreground">Scheduled</p>
                      <p className="mt-0.5 text-sm font-semibold text-foreground">{sessionDate}</p>
                    </div>
                  )}
                  {sessionNumber && (
                    <div>
                      <p className="text-xs text-muted-foreground">Session ID</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">{sessionNumber}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-2xl border border-border bg-card p-5">
                <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                  Participants
                </h3>
                <div className="mt-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-kaiva-teal text-sm font-semibold text-white">
                      {(participantName ?? 'U').charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">{participantName ?? 'You'}</p>
                      {participantRole && (
                        <p className="text-xs text-muted-foreground">
                          {participantRole}
                          <span className="ml-1 font-medium text-kaiva-teal">· You</span>
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-kaiva-coral/15 text-base font-bold text-kaiva-coral">
                      K
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">{interviewerName ?? 'Kaiva'}</p>
                      <p className="text-xs text-muted-foreground">AI Voice Interviewer</p>
                    </div>
                  </div>
                </div>
              </div>

              {guideContextPanel}
              {developerDetailsPanel}
            </div>

            {/* Right: Agenda */}
            <div className="space-y-4">
              {topics && topics.length > 0 && (
                <div className="rounded-2xl border border-border bg-card p-6">
                  <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                    Agenda
                  </h3>
                  <div className="mt-4 space-y-2">
                    {topics.map((topic, i) => (
                      <div
                        key={topic}
                        className="flex items-start gap-3 rounded-xl border border-border bg-muted/40 px-4 py-3"
                      >
                        <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-kaiva-teal/10 text-xs font-bold text-kaiva-teal">
                          {i + 1}
                        </span>
                        <span className="text-sm text-foreground">{topic}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {guideSummaryCard}
              {guideStructureCard}
              {structureSummaryCard}
            </div>
          </div>

          <div className="flex justify-end border-t border-border pt-4">
            <button
              type="button"
              onClick={() => setPreparingStep(2)}
              className="rounded-full bg-kaiva-teal px-8 py-3 text-sm font-semibold text-white transition hover:bg-kaiva-teal/90"
            >
              Next: Audio & Video →
            </button>
          </div>
        </div>
      )}

      {/* ── Step 2: Audio & Video Check ── */}
      {preparingStep === 2 && (
        <CameraCheckStep
          microphoneBadge={microphoneBadge}
          onTestMicrophone={onTestMicrophone}
          isCheckingMicrophone={isCheckingMicrophone}
          assistantMode={assistantMode}
          canChangeAssistantMode={canChangeAssistantMode}
          onChangeAssistantMode={onChangeAssistantMode}
          interviewerSource={interviewerSource}
          onOpenAgentInfo={onOpenAgentInfo}
          customConfig={customConfig}
          onBack={() => setPreparingStep(1)}
          onNext={() => setPreparingStep(3)}
        />
      )}

      {/* ── Step 3: Join ── */}
      {preparingStep === 3 && (
        <div className="flex flex-col gap-4">
          <div>
            <p className="text-xl font-semibold text-foreground">You're all set.</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Click below to join. Kaiva will greet you and guide the session from there.
            </p>
          </div>

          {/* Session summary */}
          <div className="rounded-2xl border border-border bg-gradient-to-br from-kaiva-teal/5 via-background to-kaiva-coral/5 p-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="text-xs text-muted-foreground">Session</p>
                <p className="mt-1 text-sm font-semibold text-foreground">{sessionName}</p>
              </div>
              {sessionTopic && (
                <div>
                  <p className="text-xs text-muted-foreground">Topic</p>
                  <p className="mt-1 text-sm font-semibold text-foreground">{sessionTopic}</p>
                </div>
              )}
              <div>
                <p className="text-xs text-muted-foreground">Interviewer</p>
                <p className="mt-1 text-sm font-semibold text-foreground">{interviewerName ?? 'Kaiva'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Microphone</p>
                {microphoneBadge && (
                  <span
                    className={cn(
                      'mt-1 inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold',
                      microphoneBadge.className
                    )}
                  >
                    {microphoneBadge.label}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Join + back */}
          <div className="flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={onStart}
              disabled={!canStart || !onStart}
              className="w-full rounded-full bg-kaiva-coral px-10 py-4 text-base font-semibold text-white shadow-md transition hover:bg-kaiva-coral/90 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              {isStarting ? 'Joining…' : 'Join Interview'}
            </button>
            <button
              type="button"
              onClick={() => setPreparingStep(2)}
              className="w-full rounded-full border border-border bg-card px-6 py-4 text-sm font-semibold text-foreground transition hover:bg-muted sm:w-auto"
            >
              ← Back
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const activeContent = (
    <div className="space-y-8">
      {interviewFocusCard}

      <div className="grid gap-8 lg:grid-cols-12">
        <div className="space-y-6 lg:col-span-7">
          {structureSummaryCard}

          <SectionCard title="Focus Topics" className="p-8">
            {topics && topics.length > 0 ? (
              <div className="space-y-2">
                {topics.map((topic) => (
                  <div
                    key={topic}
                    className="flex items-center justify-between rounded-xl border border-border bg-muted/60 px-3 py-2 text-xs"
                  >
                    <span className="font-semibold text-foreground">{topic}</span>
                    <span className="text-muted-foreground">
                      {transcriptText.includes(topic.toLowerCase())
                        ? 'Covered'
                        : 'Open'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No topics loaded.</p>
            )}
          </SectionCard>

          <SectionCard title="Session Context" className="p-8">
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Participant
                </p>
                <p className="mt-2 text-base font-semibold text-foreground">
                  {participantName ?? 'Participant'}
                </p>
                <p className="text-xs text-muted-foreground">{participantRole ?? 'Role'}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Interviewer
                </p>
                <p className="mt-2 text-base font-semibold text-foreground">
                  {interviewerName ?? 'Kaiva'}
                </p>
                {interviewerSource && (
                  <p className="text-xs text-muted-foreground">{interviewerSource}</p>
                )}
              </div>
            </div>
            <div className="mt-6 grid gap-6 sm:grid-cols-2">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Session</p>
                <p className="mt-2 text-sm font-semibold text-foreground">
                  {sessionNumber ?? '---'}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Date</p>
                <p className="mt-2 text-sm font-semibold text-foreground">
                  {sessionDate ?? '---'}
                </p>
              </div>
            </div>
          </SectionCard>

          {developerDetailsPanel}
        </div>

        <div className="space-y-6 lg:col-span-5">
          {guideSummaryCard}
          {guideContextPanel}
        </div>
      </div>
    </div>
  );

  const completeContent = (
    <div className="grid gap-8 lg:grid-cols-12">
      <div className="space-y-6 lg:col-span-7">
        <SectionCard title="Highlights" className="p-8">
          {resolvedHighlights.length > 0 ? (
            <div className="space-y-3">
              {resolvedHighlights.map((highlight, index) => (
                <div
                  key={`${highlight}-${index}`}
                  className="rounded-xl border border-border bg-muted/60 px-3 py-2 text-sm text-foreground"
                >
                  {highlight}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Highlights will appear once the interview is processed.
            </p>
          )}
        </SectionCard>

        <SectionCard title="Export" className="p-8">
          <div className="flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={onDownload}
              disabled={!onDownload}
              className="w-full rounded-full bg-kaiva-teal px-5 py-3 text-sm font-semibold text-white transition hover:bg-kaiva-teal/90 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              Download transcript
            </button>
            <button
              type="button"
              onClick={copyHighlights}
              disabled={resolvedHighlights.length === 0}
              className="w-full rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold text-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              {highlightCopyState === 'copied'
                ? 'Highlights copied'
                : highlightCopyState === 'error'
                  ? 'Copy failed'
                  : 'Copy highlights'}
            </button>
            {onRestart && (
              <button
                type="button"
                onClick={onRestart}
                disabled={isRestarting}
                className="w-full rounded-full border border-kaiva-coral/40 bg-card px-5 py-3 text-sm font-semibold text-kaiva-coral transition hover:bg-kaiva-coral/10 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {isRestarting ? 'Restarting...' : 'Restart Interview'}
              </button>
            )}
          </div>
        </SectionCard>

        {structureSummaryCard}

        <SectionCard title="Next Steps" className="p-8">
          <div className="space-y-2 text-sm text-muted-foreground">
            {resolvedNextSteps.map((step) => (
              <div key={step} className="rounded-xl border border-border bg-muted/60 px-3 py-2">
                {step}
              </div>
            ))}
          </div>
        </SectionCard>

        <SectionCard title="Session Summary" className="p-8">
          <div className="grid gap-6 sm:grid-cols-2">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Topic</p>
              <p className="mt-2 text-base font-semibold text-foreground">
                {sessionTopic ?? 'Handover interview'}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">Session focus</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Participant</p>
              <p className="mt-2 text-base font-semibold text-foreground">
                {participantName ?? 'Participant'}
              </p>
              <p className="text-xs text-muted-foreground">{participantRole ?? 'Role'}</p>
            </div>
          </div>
          <div className="mt-6 grid gap-6 sm:grid-cols-2">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Session</p>
              <p className="mt-2 text-sm font-semibold text-foreground">
                {sessionNumber ?? '---'}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Date</p>
              <p className="mt-2 text-sm font-semibold text-foreground">
                {sessionDate ?? '---'}
              </p>
            </div>
          </div>
        </SectionCard>

        {developerDetailsPanel}
      </div>

      <div className="lg:col-span-5">
        <div className="lg:sticky lg:top-6">
          <TranscriptPanel
            title="Full Transcript"
            subtitle="Review the full handoff conversation."
            entries={transcriptEntries}
            autoScroll={false}
            className="min-h-[520px] max-h-[calc(100vh-200px)]"
          />
        </div>
      </div>
    </div>
  );

  // ── When the interview is active, render MeetingView full-screen ──
  if (state === 'active') {
    return (
      <section className="w-full px-4 py-3 lg:px-4 xl:px-6">
        <MeetingView
          sessionName={sessionName}
          sessionNumber={sessionNumber}
          participantName={participantName}
          interviewerName={interviewerName}
          topics={topics}
          transcript={transcript}
          elapsedLabel={elapsedLabel}
          isPaused={isPaused}
          isMuted={isPaused}
          isEnding={isEnding}
          canPause={canPause}
          canEnd={canEnd}
          onPauseToggle={onPauseToggle}
          onEnd={onEnd}
          onMuteToggle={onPauseToggle}
          error={error}
        />
      </section>
    );
  }

  return (
    <section className="w-full px-4 py-3 lg:px-8 xl:px-12">
      <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-lg">
        <header className="relative overflow-hidden border-b border-border bg-gradient-to-br from-kaiva-teal/95 via-kaiva-teal/90 to-kaiva-coral/85 px-6 py-4 text-white lg:px-8 lg:py-5">
          <div className="absolute -top-20 right-10 h-40 w-40 rounded-full bg-kaiva-coral/20 blur-3xl" />
          <div className="relative flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-white/70">
                <span className="rounded-full border border-white/25 bg-white/10 px-2.5 py-0.5">
                  Interview Session
                </span>
                {templateMode && (
                  <span className="rounded-full border border-white/25 bg-white/10 px-2.5 py-0.5 text-white/85">
                    Template / Offline
                  </span>
                )}
              </div>
              <h1 className="mt-1.5 text-xl font-semibold text-white md:text-2xl">
                {sessionName}
              </h1>
              <p className="mt-0.5 text-xs text-white/70">{stateCopy[state].subtitle}</p>
            </div>
            <span
              className={cn(
                'inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold',
                isLive
                  ? 'border-kaiva-coral/60 bg-kaiva-coral/20 text-white'
                  : 'border-white/25 bg-white/10 text-white/85'
              )}
            >
              {isLive && (
                <span className="mr-2 h-2 w-2 animate-pulse rounded-full bg-kaiva-coral" />
              )}
              {stateLabel}
            </span>
          </div>
        </header>

        <div className="space-y-6 px-5 py-6 lg:px-8">
          {templateNotice && (
            <div className="rounded-xl border border-kaiva-teal/30 bg-kaiva-teal/10 p-4 text-sm text-kaiva-teal">
              {templateNotice}
            </div>
          )}
          {error && (
            <div className="rounded-xl border border-kaiva-coral/30 bg-kaiva-coral/10 p-4 text-sm text-kaiva-coral">
              {error}
            </div>
          )}
          <div className="relative">
            {(['preparing', 'active', 'complete'] as InterviewViewState[]).map((view) => (
              <div
                key={view}
                aria-hidden={state !== view}
                className={cn(
                  'transition-all duration-300 ease-out',
                  state === view
                    ? 'opacity-100 translate-y-0'
                    : 'pointer-events-none absolute inset-0 -translate-y-2 opacity-0'
                )}
              >
                {view === 'preparing'
                  ? preparingContent
                  : view === 'active'
                    ? activeContent
                    : completeContent}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default InterviewView;
